import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { authApi, saveTokens, getAccessToken, clearTokens } from '@/lib/api';

function InvoicePreview() {
  const today = new Date();
  const dueDate = new Date(today);
  dueDate.setDate(dueDate.getDate() + 14);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-sm transform -rotate-1 hover:rotate-0 transition-transform duration-300">
      {/* Paid Badge */}
      <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
        PAID
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
            <span className="text-primary font-bold text-lg">AS</span>
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">Acme Services LLC</h3>
          <p className="text-xs text-gray-500">123 Business Ave<br />Denver, CO 80202</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900">INVOICE</p>
          <p className="text-xs text-gray-500">#INV-001</p>
        </div>
      </div>

      {/* Bill To */}
      <div className="mb-4 pb-4 border-b border-gray-100">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Bill To</p>
        <p className="text-sm font-medium text-gray-900">Sarah Johnson</p>
        <p className="text-xs text-gray-500">sarah@example.com</p>
      </div>

      {/* Dates */}
      <div className="flex justify-between mb-4 text-xs">
        <div>
          <p className="text-gray-400 uppercase tracking-wide">Date</p>
          <p className="text-gray-700 font-medium">{formatDate(today)}</p>
        </div>
        <div className="text-right">
          <p className="text-gray-400 uppercase tracking-wide">Due Date</p>
          <p className="text-gray-700 font-medium">{formatDate(dueDate)}</p>
        </div>
      </div>

      {/* Line Items */}
      <div className="mb-4">
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">Website Redesign</span>
            <span className="text-gray-900 font-medium">$2,500.00</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">SEO Optimization</span>
            <span className="text-gray-900 font-medium">$750.00</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">Monthly Hosting</span>
            <span className="text-gray-900 font-medium">$50.00</span>
          </div>
        </div>
      </div>

      {/* Total */}
      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
        <span className="text-sm font-semibold text-gray-700">Total</span>
        <span className="text-xl font-bold text-primary">$3,300.00</span>
      </div>
    </div>
  );
}

type Step = 'email' | 'password';

export function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const [step, setStep] = useState<Step>(prefillEmail ? 'password' : 'email');
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [website, setWebsite] = useState('');  // Honeypot field
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(() => Boolean(getAccessToken()));

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      return;
    }

    authApi.getMe().then((response) => {
      if (response.data) {
        // Redirect to root which handles business-aware routing
        navigate('/');
      } else {
        clearTokens();
        setCheckingAuth(false);
      }
    });
  }, [navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalizedEmail = email.trim();
    const response = await authApi.checkEmail(normalizedEmail);
    setLoading(false);

    setEmail(normalizedEmail);

    if (response.data?.available) {
      setStep('password');
      return;
    }

    if (response.error) {
      // Match the mobile flow: if the availability check fails, continue and let
      // registration return the authoritative validation error.
      setStep('password');
      return;
    }

    navigate(`/login?email=${encodeURIComponent(normalizedEmail)}`);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const response = await authApi.registration({
      email,
      password1: password,
      password2: password,
      website,
    });
    setLoading(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      // Registration successful - save tokens and go to onboarding
      saveTokens(response.data.tokens);
      navigate('/onboarding');
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      const response = await authApi.googleLogin({
        access_token: tokenResponse.access_token,
      });
      setLoading(false);

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        saveTokens(response.data.tokens);
        if (response.data.user.businesses.length === 0) {
          navigate('/onboarding');
        } else {
          navigate(`/${response.data.user.businesses[0].id}/dashboard`);
        }
      }
    },
    onError: () => {
      setError('Google signup failed. Please try again.');
    },
  });

  if (checkingAuth) {
    return null;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/header.svg" alt="InvoiceOwl" className="h-14 mx-auto" />
          </div>

          <Card>
            <CardHeader className="text-center pb-4">
              <h2 className="text-2xl font-semibold">Start with InvoiceOwl</h2>
              <p className="text-muted-foreground mt-1">Choose a plan and subscribe today.</p>
              <p className="text-muted-foreground text-sm">Cancel anytime.</p>
            </CardHeader>
            <CardContent>
              {step === 'email' ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Checking...' : 'Continue'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      disabled
                      className="bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Create Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={4}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  {/* Honeypot - hidden from humans */}
                  <div className="absolute left-[-9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading || password.length < 4}>
                    {loading ? 'Creating account...' : 'Create account'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setStep('email');
                      setError('');
                    }}
                  >
                    Back
                  </Button>
                </form>
              )}

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <Button variant="outline" type="button" className="w-full" onClick={() => googleLogin()} disabled={loading}>
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Google
                  </Button>
                </div>
              </div>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Already registered?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Invoice Preview & Benefits */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-12 items-center justify-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col items-center">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Get paid 4x faster with<br />professional invoices
            </h2>
            <p className="text-muted-foreground">
              Join thousands of businesses simplifying their billing
            </p>
          </div>

          {/* Invoice Preview */}
          <div className="relative mb-8">
            <InvoicePreview />
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 gap-4 mb-8 w-full max-w-md">
            <div className="flex items-start gap-3 bg-white/50 rounded-lg p-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm text-foreground">Get paid on time</p>
                <p className="text-xs text-muted-foreground">Automated reminders</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white/50 rounded-lg p-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm text-foreground">Look professional</p>
                <p className="text-xs text-muted-foreground">Branded invoices</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white/50 rounded-lg p-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm text-foreground">Invoice anywhere</p>
                <p className="text-xs text-muted-foreground">Mobile friendly</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white/50 rounded-lg p-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm text-foreground">E-sign estimates</p>
                <p className="text-xs text-muted-foreground">Faster approvals</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-8 text-center">
            <div>
              <p className="text-2xl font-bold text-primary">50,000+</p>
              <p className="text-sm text-muted-foreground">Businesses trust us</p>
            </div>
            <div className="w-px bg-border" />
            <div>
              <p className="text-2xl font-bold text-primary">3 days</p>
              <p className="text-sm text-muted-foreground">Avg. time to get paid</p>
            </div>
            <div className="w-px bg-border" />
            <div>
              <p className="text-2xl font-bold text-primary">$2B+</p>
              <p className="text-sm text-muted-foreground">Invoices processed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
